import type { ApolloCache } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import classNames from 'classnames';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from '../../utils/router/v3Bridge';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import MetaList, { MetaListItem } from '../../components/MetaList';
import type {
  Comment_CommentFragment,
  Comment_PostFragment,
  CreateCommentReactionMutation,
  CreateCommentReactionMutationVariables,
  DeleteCommentMutation,
  DeleteCommentMutationVariables,
  DeleteCommentReactionMutation,
  DeleteCommentReactionMutationVariables,
  DeleteThreadMutation,
  DeleteThreadMutationVariables,
  UpdateCommentMutation,
  UpdateCommentMutationVariables,
  UpdateCommentReactionQuery,
  UpdateCommentReactionQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import editIcon from '../../icons/edit.svg';
import linkIcon from '../../icons/link.svg';
import trashIcon from '../../icons/trash.svg';
import reactionIcon from '../../icons/reaction.svg';
import UIActions from '../../ui/UIActions';
import updateApolloList from '../../utils/graphql/updateApolloList';
import commentThreadHash from '../../utils/url/commentThreadHash';
import postPath from '../../utils/url/postPath';
import userPath from '../../utils/url/userPath';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import CommentAuthor from './CommentAuthor';
import CommentButtons from './CommentButtons';
import CommentInput from './CommentInput';
import CommentMenu from './CommentMenu';
import CommentPreview from './CommentPreview';
import CommentReactionList from './CommentReactionList';
import isCommentContentEmpty from './isCommentContentEmpty';
import showFullPageSheet from './showFullPageSheet';

const { __slab } = window;

const POST_FRAGMENT = gql`
  fragment Comment_post on Post {
    id
    ...postPath_post
  }
  ${postPath.fragments.post}
`;

const MAX_LINE_COUNT = 4;
const EXPANDED_MAX_LINE_COUNT = 8;
const LARGE_COMMENT_LENGTH_THRESHOLD = 400;

const DELETE_THREAD_MUTATION = gql`
  mutation deleteThread($id: ID!) {
    deleteThread(threadId: $id) {
      id
    }
  }
`;

const DELETE_COMMENT_MUTATION = gql`
  mutation deleteComment($id: ID!) {
    deleteComment(commentId: $id) {
      id
    }
  }
`;

const UPDATE_COMMENT_MUTATION = gql`
  mutation updateComment($id: ID!, $content: String!) {
    updateComment(commentId: $id, content: $content) {
      id
    }
  }
`;

const COMMENT_FRAGMENT = gql`
  fragment Comment_comment on Comment {
    id
    content
    author {
      id
      ...userPath_user
      ...MagneticWallItemAvatar_user
      ...CommentAuthor_author
    }
    reactions {
      ...CommentReactionList_reactions
    }
    insertedAt
  }
  ${userPath.fragments.user}
  ${MagneticWallItemAvatar.fragments.user}
  ${CommentAuthor.fragments.author}
  ${CommentReactionList.fragments.reactions}
`;

const CREATE_COMMENT_REACTION_MUTATION = gql`
  mutation createCommentReaction($commentId: ID!, $emoji: String!) {
    commentReaction: createCommentReaction(
      commentId: $commentId
      emoji: $emoji
    ) {
      ...CommentReactionList_reactions
    }
  }
  ${CommentReactionList.fragments.reactions}
`;

const DELETE_COMMENT_REACTION_MUTATION = gql`
  mutation deleteCommentReaction($id: ID!) {
    commentReaction: deleteCommentReaction(id: $id) {
      ...CommentReactionList_reactions
    }
  }
  ${CommentReactionList.fragments.reactions}
`;

const UPDATE_COMMENT_REACTION_QUERY = gql`
  query updateCommentReaction($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        comments {
          id
          reactions {
            id
          }
        }
      }
    }
  }
`;

const updateCommentReactionList = <
  Data extends { [key in NewItemKey]?: NewItem | null },
  NewItemKey extends string,
  NewItem extends Item,
  Item extends { id?: string | null } | string,
>(
  { threadId, commentId }: { threadId: string; commentId: string },
  prev: UpdateCommentReactionQuery,
  data: Data,
  itemKey: string,
  add = true,
) => ({
  ...prev,
  post: prev.post && {
    ...prev.post,
    threads: prev.post.threads?.map((thread) =>
      thread.id !== threadId
        ? thread
        : {
            ...thread,
            comments: thread.comments?.map((comment) =>
              comment.id !== commentId
                ? comment
                : updateApolloList(comment, data, 'reactions', itemKey, add),
            ),
          },
    ),
  },
});

function updateCommentReactionQueryList<
  Cache,
  Response extends { data?: Data | null },
  Data extends { [key in NewItemKey]?: NewItem | null },
  NewItemKey extends string,
  NewItem extends Item,
  Item extends { id?: string | null } | string,
>(
  {
    postId,
    threadId,
    commentId,
  }: { postId: string; threadId: string; commentId: string },
  dataKey: string,
  add = true,
) {
  return (proxy: ApolloCache<Cache>, { data }: Response) => {
    try {
      const query = UPDATE_COMMENT_REACTION_QUERY;
      const prev = proxy.readQuery<
        UpdateCommentReactionQuery,
        UpdateCommentReactionQueryVariables
      >({ query, variables: { postId } });
      if (prev && data) {
        const updated = updateCommentReactionList(
          { threadId, commentId },
          prev,
          data,
          dataKey,
          add,
        );
        proxy.writeQuery({ query, variables: { postId }, data: updated });
      }
    } catch (e) {
      // readQuery failed
    }
  };
}

interface CommentProps {
  comment: Comment_CommentFragment;
  post: Comment_PostFragment;
  onResize: () => void;
  threadId: string;
  isFirst: boolean;
  isExpanded: boolean;
  initialShowingMore?: boolean;
}

const Comment = ({
  comment,
  onResize,
  threadId,
  post,
  isFirst,
  isExpanded,
  initialShowingMore = false,
}: CommentProps) => {
  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isShowingMore, setIsShowingMore] = useState(initialShowingMore);
  const [isShowingButtonGroup, setIsShowingButtonGroup] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [editContent, setEditContent] = useState<Op[] | null>(null);
  const [canBeClamped, setCanBeClamped] = useState<boolean | null>(null);

  const currentUser = useAppSelector((state) => state.session.user);

  const [deleteThreadMutation] = useMutation<
    DeleteThreadMutation,
    DeleteThreadMutationVariables
  >(DELETE_THREAD_MUTATION);

  const [deleteCommentMutation] = useMutation<
    DeleteCommentMutation,
    DeleteCommentMutationVariables
  >(DELETE_COMMENT_MUTATION);

  const [updateCommentMutation] = useMutation<
    UpdateCommentMutation,
    UpdateCommentMutationVariables
  >(UPDATE_COMMENT_MUTATION);

  const [createCommentReactionMutation] = useMutation<
    CreateCommentReactionMutation,
    CreateCommentReactionMutationVariables
  >(CREATE_COMMENT_REACTION_MUTATION);

  const [deleteCommentReactionMutation] = useMutation<
    DeleteCommentReactionMutation,
    DeleteCommentReactionMutationVariables
  >(DELETE_COMMENT_REACTION_MUTATION);

  const deleteThread = () => {
    dispatch(
      UIActions.confirmModal({
        title: 'Delete entire thread?',
        message: 'All comments in the thread will be deleted.',
        confirmButtonText: 'Delete',
        onConfirm: () => deleteThreadMutation({ variables: { id: threadId } }),
      }),
    );
  };

  const deleteComment = () =>
    deleteCommentMutation({ variables: { id: comment.id } });

  const updateComment = (content: string) =>
    updateCommentMutation({ variables: { id: comment.id, content } });

  const createCommentReaction = (emoji: string) =>
    createCommentReactionMutation({
      variables: { commentId: comment.id, emoji },
      update: updateCommentReactionQueryList(
        { threadId, postId: post.id, commentId: comment.id },
        'commentReaction',
        true,
      ),
    });

  const deleteCommentReaction = (id: string) =>
    deleteCommentReactionMutation({
      variables: { id },
      update: updateCommentReactionQueryList(
        { threadId, postId: post.id, commentId: comment.id },
        'commentReaction',
        false,
      ),
    });

  const onEnterEdit = () => {
    setIsEditing(true);
    setEditContent(comment.content);
    onResize();
  };

  const onCancelEdit = () => {
    setIsEditing(false);
    onResize();
  };

  const onCopyLink = () => {
    const url = `https://${__slab.ORG_HOST}${postPath(post)}${commentThreadHash(
      threadId,
    )}`;

    navigator.clipboard.writeText(url).then(() => {
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Link copied to clipboard',
        }),
      );
    });
  };

  const onEditChange = (newContent: Op[]) => {
    setEditContent(newContent);
    onResize();
  };

  const onEmojiPickerOpenChange = (open: boolean) => {
    setIsEmojiPickerOpen(open);
  };

  const onSave = async () => {
    if (!editContent?.length) {
      return;
    }

    setIsSaving(true);

    try {
      await updateComment(JSON.stringify(editContent));
      setIsEditing(false);
      onResize();
    } catch {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, we could not save the comment. Please try again or contact support.',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getCommentMenuItems = (includingReactionItem: boolean) => {
    const isAuthor = comment.author.id === currentUser.id;
    const menuItems = [];

    if (isAuthor) {
      menuItems.push(
        <MenuItem key="edit" icon={editIcon} onClick={onEnterEdit}>
          Edit comment
        </MenuItem>,
      );
    }

    if (isFirst) {
      menuItems.push(
        <MenuItem key="copy" icon={linkIcon} onClick={onCopyLink}>
          Copy link
        </MenuItem>,
      );
    }

    if (includingReactionItem) {
      menuItems.push(
        <MenuItem
          key="reaction"
          icon={reactionIcon}
          onClick={() => onEmojiPickerOpenChange(true)}
        >
          Add reaction
        </MenuItem>,
      );
    }

    if (isAuthor) {
      if (menuItems.length > 0) {
        menuItems.push(<MenuSeparator key="separator" />);
      }
      if (isFirst) {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete_thread"
            danger
            onClick={deleteThread}
          >
            Delete thread
          </MenuItem>,
        );
      } else {
        menuItems.push(
          <MenuItem
            icon={trashIcon}
            key="delete"
            danger
            onClick={deleteComment}
          >
            Delete comment
          </MenuItem>,
        );
      }
    }

    return menuItems;
  };

  const toggleShowMore = () => {
    setIsShowingMore(!isShowingMore);
    onResize();
  };

  const toggleCommentReaction = (emoji: string) => {
    const reaction = comment.reactions.find(
      (item) => item.emoji === emoji && item.author.id === currentUser.id,
    );

    if (reaction) {
      deleteCommentReaction(reaction.id);
    } else {
      dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
      createCommentReaction(emoji);
    }
  };

  const renderCommentActions = () => {
    const actions = [];

    if (canBeClamped && !isEditing) {
      actions.push(
        <MetaListItem key="show-more">
          <Button variant="link" size="micro" onClick={toggleShowMore}>
            {isShowingMore ? 'Show less' : 'Show more'}
          </Button>
        </MetaListItem>,
      );
    }
    if (isEditing) {
      actions.push(
        <MetaListItem key="edit">
          <Button
            disabled={isSaving || isCommentContentEmpty(editContent)}
            variant="link"
            size="micro"
            onClick={onSave}
          >
            Save
          </Button>
        </MetaListItem>,
        <MetaListItem key="cancel">
          <Button variant="link" size="micro" onClick={onCancelEdit}>
            Cancel
          </Button>
        </MetaListItem>,
      );
    }

    if (!actions.length) {
      return null;
    }

    return (
      <div className="comment-actions">
        <MetaList>{actions}</MetaList>
      </div>
    );
  };

  const avatar = <MagneticWallItemAvatar user={comment.author} />;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const fullPageSheet = showFullPageSheet();
  const commonButtonProps = {
    emojiPickerOpen: isEmojiPickerOpen,
    onEmojiPickerOpenChange,
    toggleCommentReaction,
  };

  return (
    <div
      className={classNames('comment', {
        'comment-large':
          isExpanded &&
          isShowingMore &&
          new Delta(comment.content).length() > LARGE_COMMENT_LENGTH_THRESHOLD,
      })}
    >
      <div className="comment-left">
        {isExpanded ? (
          <Link to={userPath(comment.author)}>{avatar}</Link>
        ) : (
          avatar
        )}
      </div>
      <div
        className={classNames('comment-right', {
          'comment-right-sheet': fullPageSheet,
        })}
        onMouseEnter={() => setIsShowingButtonGroup(true)}
        onMouseLeave={() => setIsShowingButtonGroup(false)}
      >
        {isExpanded && (
          <div className="comment-meta">
            <CommentAuthor
              isFirst={isFirst}
              isExpanded={isExpanded}
              author={comment.author}
              insertedAt={comment.insertedAt}
              threadId={threadId}
            />
          </div>
        )}
        <div className="comment-content">
          {isEditing && editContent ? (
            <CommentInput
              autoFocus
              initialContent={editContent}
              onChange={onEditChange}
              onSave={onSave}
            />
          ) : (
            <CommentPreview
              collapseLines={!isExpanded}
              content={comment.content}
              link={isExpanded}
              showHoverCard={isExpanded}
              maxLines={isExpanded ? EXPANDED_MAX_LINE_COUNT : MAX_LINE_COUNT}
              disableLineClamp={isExpanded && isShowingMore}
              onCanBeClampedChange={setCanBeClamped}
            />
          )}
        </div>
        {!isEditing && isExpanded && (
          <div className="comment-button">
            {fullPageSheet ? (
              <CommentMenu
                menuItems={getCommentMenuItems(true)}
                {...commonButtonProps}
              />
            ) : (
              <CommentButtons
                showing={isShowingButtonGroup}
                menuItems={getCommentMenuItems(false)}
                {...commonButtonProps}
              />
            )}
          </div>
        )}
        {isExpanded && (
          <>
            {renderCommentActions()}
            {comment.reactions && comment.reactions.length > 0 && (
              <div className="comment-reactions">
                <CommentReactionList
                  reactions={comment.reactions}
                  toggleCommentReaction={toggleCommentReaction}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

Comment.fragments = {
  post: POST_FRAGMENT,
  comment: COMMENT_FRAGMENT,
};

export default Comment;
